import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { ActionWithPayload, SYNC_SUFFIX } from "@obg/ngrx-utils";

import { AppError } from "../../app-error/app-error.model";
import { CustomerNotVerifiedData } from "../../auth/session.model";

import {
	SmartIdAuthTransactionRequest,
	SmartIdInitializeSuccessPayload,
	SmartIdVerificationRequest,
	SmartIdVerificationStatusRequest,
	SmartIdVerificationStatusResponse,
} from "./smart-id-auth-transaction.model";

export const ACTION_PREFIX = "[Smart-ID Auth Transaction]";
export const SMART_ID_AUTH_TRANSACTION_ACTION_TYPE = {
	/**
	 * Dispatched to retrieve verification status for a specific token.
	 */
	getVerificationStatus: `${ACTION_PREFIX} Get Verification Status`,
	getVerificationStatusSuccess: `${ACTION_PREFIX} Get Verification Status Success`,
	getVerificationStatusPending: `${ACTION_PREFIX} Get Verification Status ${SYNC_SUFFIX}`,
	getVerificationStatusFail: `${ACTION_PREFIX} Get Verification Status Fail`,

	/**
	 * Starts verification and returns required autostartToken and transactionId
	 */
	initializeVerification: `${ACTION_PREFIX} Initialize Verification`,
	initializeVerificationSuccess: `${ACTION_PREFIX} Initialize Verification Success`,
	initializeVerificationFail: `${ACTION_PREFIX} Initialize Verification Fail`,

	/**
	 * Starts auth transaction and returns required autostartToken and transactionId
	 */
	initializeAuthTransaction: `${ACTION_PREFIX} Initialize Auth Transaction`,
	initializeAuthTransactionSuccess: `${ACTION_PREFIX} Initialize Auth Transaction Success`,
	initializeAuthTransactionFail: `${ACTION_PREFIX} Initialize Auth Transaction Fail`,

	/**
	 * Cancels last verification
	 */
	cancelVerification: `${ACTION_PREFIX} Cancel Verification ${SYNC_SUFFIX}`,
	cancelLastVerification: `${ACTION_PREFIX} Cancel Last Verification`,
	cancelLastVerificationSuccess: `${ACTION_PREFIX} Cancel Last Verification Success`,
	cancelLastVerificationFail: `${ACTION_PREFIX} Cancel Last Verification Fail`,

	reset: `${ACTION_PREFIX} Reset ${SYNC_SUFFIX}`,
	resetVerificationStatus: `${ACTION_PREFIX} Reset Verification Status ${SYNC_SUFFIX}`,
};

@Injectable({
	providedIn: "root",
})
export class SmartIdAuthTransactionAction {
	getVerificationStatus(payload: SmartIdVerificationStatusRequest): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
			payload,
		};
	}

	getVerificationStatusSuccess(payload: SmartIdVerificationStatusResponse): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusSuccess,
			payload,
		};
	}

	getVerificationStatusPending(payload: SmartIdVerificationStatusResponse): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusPending,
			payload,
		};
	}

	getVerificationStatusFail(error: AppError | Error): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail,
			payload: error,
		};
	}

	initializeAuthTransaction(payload?: SmartIdAuthTransactionRequest): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction,
			payload,
		};
	}

	initializeAuthTransactionSuccess(payload: SmartIdInitializeSuccessPayload): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionSuccess,
			payload,
		};
	}

	initializeAuthTransactionFail(error: AppError | Error): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail,
			payload: error,
		};
	}

	initializeVerification(payload: SmartIdVerificationRequest): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerification,
			payload,
		};
	}

	initializeVerificationSuccess(payload: SmartIdInitializeSuccessPayload): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationSuccess,
			payload,
		};
	}

	initializeVerificationFail(error: AppError | Error): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail,
			payload: error,
		};
	}

	cancelVerification(payload: CustomerNotVerifiedData): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelVerification,
			payload,
		};
	}

	cancelLastVerification(): Action {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification,
		};
	}

	cancelLastVerificationSuccess(): Action {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerificationSuccess,
		};
	}

	cancelLastVerificationFail(error: AppError | Error): ActionWithPayload {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerificationFail,
			payload: error,
		};
	}

	resetVerificationStatus(): Action {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.resetVerificationStatus,
		};
	}

	reset(): Action {
		return {
			type: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.reset,
		};
	}
}
